import React from 'react';
import { Query as ApolloQuery } from '~/lazy_apollo/client/react/components';
import type { DocumentNode, OperationVariables, QueryFunctionOptions, TypedDocumentNode } from '~/lazy_apollo/client';
import type { ExtractDataType, ExtractVariablesType } from '~/lazy_apollo/client/react/components';
import type { QueryChildren, ServerSideQuerySubtle } from '../utils/apolloOptimizations';

export type FetchPolicy = 'cache-and-network' | 'cache-first' | 'network-only';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface QueryProps<TQuery extends DocumentNode | TypedDocumentNode<any, any>>
  extends QueryFunctionOptions<ExtractDataType<TQuery>, ExtractVariablesType<TQuery>> {
  // Please set only one of the following: ApolloQuery or ServerSideQuerySubtle.
  // This could be written as a union, but that triggers a strange IDEA bug.
  QueryComponent: [typeof ApolloQuery<TQuery>, typeof ServerSideQuerySubtle<TQuery>][number];
  children: QueryChildren<ExtractDataType<TQuery>, ExtractVariablesType<TQuery>> | null;
  query: TQuery;
  fetchPolicy: FetchPolicy;
  nextFetchPolicy: FetchPolicy;
  notifyOnNetworkStatusChange: boolean;
  partialRefetch: boolean;
  returnPartialData: boolean;
  ssr: boolean;
  variables: ExtractVariablesType<TQuery>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class Query<TQuery extends DocumentNode | TypedDocumentNode<any, any>> extends React.PureComponent<QueryProps<TQuery>> {
  // Default ApolloQuery options
  // https://www.apollographql.com/docs/react/api/@apollo/client
  // https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration/
  static readonly defaultProps = {
    children: null,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    partialRefetch: true,
    QueryComponent: ApolloQuery,
    returnPartialData: false,
    ssr: true,
    variables: undefined,
  };

  render() {
    const { children, QueryComponent, query, variables, ...props } = this.props;
    // Replace variables set to undefined literal with "null" so that they are still sent to the server & included in the query cache
    let cleanVariables: ExtractVariablesType<TQuery> | undefined;
    if (variables) {
      const cleanVariablesTemp: OperationVariables = {};
      Object.keys(variables).forEach((key) => {
        if (typeof variables[key] === 'undefined') {
          cleanVariablesTemp[key] = null;
        } else {
          cleanVariablesTemp[key] = variables[key];
        }
      });
      cleanVariables = cleanVariablesTemp as ExtractVariablesType<TQuery>;
    }

    return (
      <QueryComponent
        query={query}
        variables={cleanVariables}
        {...props}
      >
        {(params) => {
          if (typeof children === 'function') {
            return children(params);
          }
          return null;
        }}
      </QueryComponent>
    );
  }
}

export default Query;
